import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Check, X, ArrowLeft } from "lucide-react"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Item } from "@/types/purchase"
import { SortableItem } from "@/components/SortableItem"
import type { BulkApplyTargets } from "@/hooks/useSameProductBulkEdit"

interface ProductListProps {
  items: Item[];
  setItems: (items: Item[] | ((prevItems: Item[]) => Item[])) => void;
  totalAmount: number;
  calculatedShippingFee: number;
  paymentAmount: number;
  resetOrder?: () => void;
  removeItemByIndex?: (index: number) => void;
  updateItemQuantity?: (index: number, quantity: number) => void;
  updateItem?: (index: number, data: Partial<Item>) => void; // 통합 업데이터 함수
  // 동일 상품 일괄 수정(opt-in) — 둘 다 optional: 미주입 시 기존과 100% 동일
  getBulkApplyTargets?: (item: Item) => BulkApplyTargets;
  onSaveWithSameProducts?: (index: number, data: Partial<Item>) => Promise<void> | void;
  showResetButton?: boolean;
}

export function ProductList({
  items,
  setItems,
  totalAmount,
  calculatedShippingFee,
  paymentAmount,
  resetOrder,
  removeItemByIndex,
  updateItemQuantity,
  updateItem, // 통합 업데이터 함수
  getBulkApplyTargets,
  onSaveWithSameProducts,
  showResetButton = true
}: ProductListProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<Item | null>(null);
  // 동일 상품 일괄 적용 체크박스 — 편집 세션마다 기본 해제.
  // bulkTargets는 startEditing 시점 스냅샷(편집 중 카운트가 live로 출렁여 행이
  // 나타났다 사라지는 layout shift 방지 — 저장 시점 드리프트는 훅/서비스 재검증이 커버).
  const [bulkApply, setBulkApply] = useState(false);
  const [bulkTargets, setBulkTargets] = useState<BulkApplyTargets | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setItems((prevItems: Item[]) => {
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        // 인덱스 검증: id는 렌더 시점 인덱스라 source가 그 사이 바뀌면 stale일 수 있음.
        // 범위를 벗어나면 변경 없이 prev 반환(배열 손상 방지).
        if (
          Number.isNaN(oldIndex) || Number.isNaN(newIndex) ||
          oldIndex < 0 || oldIndex >= prevItems.length ||
          newIndex < 0 || newIndex >= prevItems.length
        ) {
          return prevItems;
        }
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const handleItemQuantityChange = (index: number, newQuantity: number) => {
    if (updateItemQuantity) {
      updateItemQuantity(index, newQuantity);
    } else {
      const newItems = [...items];
      newItems[index].quantity = newQuantity;
      setItems(newItems);
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditItem(items[index]);
    // 두 콜백이 모두 주입됐을 때만 일괄 적용 UI 활성 (한쪽만 있으면 조용한 폴백 함정)
    setBulkTargets(getBulkApplyTargets && onSaveWithSameProducts ? getBulkApplyTargets(items[index]) : null);
    setBulkApply(false);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditItem(null);
    setBulkApply(false);
    setBulkTargets(null);
  };

  const saveEdit = (index: number) => {
    if (!editItem || !editItem.name || editItem.price === undefined || editItem.quantity === undefined) return;

    const originalItem = items[index];
    const changes: Partial<Item> = {};

    if (editItem.name !== originalItem.name) {
      changes.name = editItem.name;
    }
    if (editItem.price !== originalItem.price) {
      changes.price = editItem.price;
    }
    if (editItem.quantity !== originalItem.quantity) {
      changes.quantity = editItem.quantity;
    }

    if (Object.keys(changes).length > 0) {
      if (bulkApply && onSaveWithSameProducts) {
        // 동일 상품 일괄 적용 경로 — 단일 수정 + 다른 '미지정' 주문 전파는 훅이 수행
        onSaveWithSameProducts(index, changes);
      } else if (updateItem) {
        // 통합된 updateItem 함수 사용
        updateItem(index, changes);
      } else {
        // updateItem이 없을 경우를 대비한 폴백 (기존 로직과 유사하게)
        const newItems = [...items];
        newItems[index] = { ...originalItem, ...changes };
        setItems(newItems);
      }
    }

    setEditingIndex(null);
    setEditItem(null);
    setBulkApply(false);
    setBulkTargets(null);
  };

  const deleteItem = (index: number) => {
    if (removeItemByIndex) {
      removeItemByIndex(index);
    } else {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-gray-200">
      <h3 className="text-base font-semibold text-gray-700 mb-3">상품 목록</h3>
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-6 px-4 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/70">
          <p className="font-medium">상품 목록이 비어있습니다.</p>
          <p className="mt-1">위에서 상품을 추가해주세요.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((_, index) => index)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2.5">
              {items.map((item: Item, index: number) => (
                editingIndex === index ? (
                  // EDITING STATE UI - Keep this compact as it's temporary
                  // 체크박스 미노출 시 박스 모델이 기존과 동일하도록 flex 배치만 안쪽 래퍼로 내림.
                  <div key={`editing-${index}`} className="p-3 border border-indigo-300 rounded-lg bg-indigo-50 shadow-sm">
                    <div className="flex items-center gap-2">
                    <Input
                      value={editItem?.name || ""}
                      onChange={(e) => setEditItem({ ...editItem!, name: e.target.value })}
                      className="flex-1 h-9 text-sm border-indigo-400 focus:ring-indigo-500"
                      placeholder="상품명"
                    />
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={editItem?.price || ""}
                        onChange={(e) => setEditItem({ ...editItem!, price: Number(e.target.value) })}
                        className="w-24 text-center h-9 text-sm border-indigo-400 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="가격"
                      />
                      <Input
                        type="number"
                        value={editItem?.quantity || ""}
                        onChange={(e) => {
                          const quantity = Number(e.target.value);
                          if (quantity >= 1) {
                            setEditItem({ ...editItem!, quantity });
                          }
                        }}
                        className="w-16 text-center h-9 text-sm border-indigo-400 focus:ring-indigo-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="수량"
                      />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-green-600 hover:bg-green-100"
                        onClick={() => saveEdit(index)}
                        aria-label="저장"
                      >
                        <Check className="h-4.5 w-4.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={cancelEditing}
                        className="h-9 w-9 text-gray-500 hover:bg-gray-100"
                        aria-label="취소"
                      >
                        <X className="h-4.5 w-4.5" />
                      </Button>
                    </div>
                    </div>
                    {bulkTargets && bulkTargets.unassigned > 0 && (
                      <div className="mt-2.5 pt-2 border-t border-indigo-200 flex items-start gap-1.5">
                        <Checkbox
                          id={`bulk-apply-${index}`}
                          checked={bulkApply}
                          onCheckedChange={(checked) => setBulkApply(checked === true)}
                          // 이 레포의 shadcn Checkbox 기본 체크 색(bg-primary)은 죽은 클래스 — 인디고 명시 지정 필수
                          className="mt-px border-indigo-400 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 data-[state=checked]:text-white"
                        />
                        <label
                          htmlFor={`bulk-apply-${index}`}
                          className="min-w-0 cursor-pointer select-none text-xs leading-4"
                        >
                          <span className="font-medium text-gray-800">
                            같은 날짜 '미지정' 주문 {bulkTargets.unassigned}건의 같은 상품에도 적용
                          </span>
                          <span className="mt-0.5 block text-[11px] text-gray-500">
                            상품명·가격만 변경 (수량 제외)
                            {bulkTargets.progressed > 0 && ` · 입금·출고된 주문 ${bulkTargets.progressed}건 제외`}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <SortableItem
                    key={index}
                    item={item}
                    index={index}
                    onEdit={startEditing}
                    onDelete={deleteItem}
                    onQuantityChange={handleItemQuantityChange}
                  />
                )
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Totals Section - only show if there are items */}
      {items.length > 0 && (
        <div className="mt-5 pt-5 border-t border-gray-200 space-y-2.5 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>총 구매 건수:</span>
            <span className="font-medium text-gray-800">
              {items.reduce((sum, item) => sum + item.quantity, 0)}건
            </span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>상품 금액 합계:</span>
            <span className="font-medium text-gray-800">{totalAmount.toLocaleString()}원</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>배송비:</span>
            <span className="font-medium text-gray-800">
              {calculatedShippingFee === 0
                ? "무료"
                : `${calculatedShippingFee.toLocaleString()}원`
              }
            </span>
          </div>
          <div className="flex justify-between text-base font-semibold text-gray-800 pt-2.5 border-t border-gray-300 mt-2.5">
            <span>총 결제 예정 금액:</span>
            <span className="text-indigo-600">{paymentAmount.toLocaleString()}원</span>
          </div>
        </div>
      )}
      
      {items.length > 0 && resetOrder && showResetButton && (
        <div className="mt-5 text-center">
          <Button
            type="button"
            variant="outline"
            onClick={resetOrder}
            className="text-sm px-4 py-2 text-gray-600 border-gray-300 hover:bg-gray-100 hover:border-gray-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            목록 초기화
          </Button>
        </div>
      )}
    </div>
  );
} 